import React, { useState, useRef } from 'react';
import { useOrganizationAppStore } from '@/hooks/useOrganizationAppStore';
import { useAppsManager } from '@/contexts/AppsManager';
import { useWindowManager } from '@/contexts/WindowManager';
import { useWindowResponsive } from '@/hooks/useWindowResponsive';
import { AppStoreSidebar } from '@/components/ui/app-store-sidebar';
import { AppStoreHero } from '@/components/ui/app-store-hero';
import { AppStoreSection } from '@/components/ui/app-store-section';
import { AppStoreSearchBar } from '@/components/ui/app-store-search-bar';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AppStoreWindow() {
  const containerRef = useRef<HTMLDivElement>(null);
  const responsive = useWindowResponsive(containerRef);
  const appStore = useOrganizationAppStore();
  const {
    addToDock: addToDockAction,
    installApp: installAppAction,
    uninstallApp: uninstallAppAction,
    apps,
  } = useAppsManager();
  const { openWindow } = useWindowManager();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('discover');
  const [sidebarOpen, setSidebarOpen] = useState(!responsive.shouldCollapseSidebar);

  // Filter apps based on search and category
  const filteredApps = appStore.apps.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === 'discover' ||
      app.category.toLowerCase() === selectedCategory.toLowerCase();

    return matchesSearch && matchesCategory;
  });

  // Get featured apps (for hero section) — most recently updated first
  const sortedByRecent = [...appStore.apps].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
  const featuredApps = sortedByRecent.slice(0, 3);

  // Get recently updated apps
  const recentApps = sortedByRecent.slice(0, 6);

  const handleAppAction = async (action: string, appId: string, appName: string) => {
    switch (action) {
      case 'install':
        // Use AppsManager's install action for consistency
        installAppAction(appId);
        break;
      case 'uninstall':
        // Use AppsManager's uninstall action for consistency
        uninstallAppAction(appId);
        break;
      case 'addToDock':
        // Use AppsManager's addToDock which triggers optimistic updates
        addToDockAction(appId);
        break;
      case 'open':
        const app = apps.find((a) => a.id === appId);
        if (app) {
          const windowId = app.component_type.replace('Window', '').toLowerCase();
          openWindow(windowId, appName, app.component);
          await appStore.updateLastUsed(appId);
        }
        break;
    }
  };

  if (appStore.loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-background via-background/95 to-background/90">
        <div className="text-muted-foreground text-lg">Loading App Store...</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full flex bg-gradient-to-br from-zinc-950 via-zinc-900 to-black backdrop-blur-sm"
    >
      {/* Sidebar - Adaptive based on window size */}
      {responsive.shouldCollapseSidebar ? (
        <>
          {/* Mobile/Narrow: Overlay Sidebar */}
          {sidebarOpen && (
            <div className="absolute inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-background/80 backdrop-blur-sm"
                onClick={() => setSidebarOpen(false)}
              />
              <div className="relative w-64 h-full">
                <AppStoreSidebar
                  selectedCategory={selectedCategory}
                  onCategorySelect={(category) => {
                    setSelectedCategory(category);
                    setSidebarOpen(false);
                  }}
                  responsive={responsive}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        /* Wide: Persistent Sidebar */
        <AppStoreSidebar
          selectedCategory={selectedCategory}
          onCategorySelect={setSelectedCategory}
          responsive={responsive}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Search */}
        <div
          className={
            responsive.isNarrow ? 'p-3 pb-2' : responsive.isMedium ? 'p-4 pb-3' : 'p-6 pb-4'
          }
        >
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <div className="flex items-center gap-3">
              {responsive.shouldCollapseSidebar && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(true)}
                  className="shrink-0"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              )}
              <div>
                <h1
                  className={`font-light text-zinc-100 tracking-wide ${
                    responsive.isNarrow ? 'text-lg' : responsive.isMedium ? 'text-xl' : 'text-2xl'
                  }`}
                >
                  App Store
                </h1>
                <p
                  className={`text-zinc-400 mt-0.5 font-light tracking-wide ${
                    responsive.isNarrow ? 'text-xs' : 'text-sm'
                  }`}
                >
                  Discover amazing apps for your workspace
                </p>
              </div>
            </div>
          </div>

          <AppStoreSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            responsive={responsive}
          />
        </div>

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-auto pb-3 lg:pb-6 ${
            responsive.isNarrow ? 'px-3' : responsive.isMedium ? 'px-4' : 'px-6'
          }`}
        >
          {selectedCategory === 'discover' && !searchQuery && (
            <>
              {/* Hero Section */}
              <AppStoreHero
                apps={featuredApps}
                onAppAction={handleAppAction}
                appStore={appStore}
                responsive={responsive}
                installationProgress={appStore.installationProgress}
              />

              {/* Recent Updates Section */}
              <AppStoreSection
                title="Recently Updated"
                subtitle="Fresh apps and updates for you"
                apps={recentApps}
                onAppAction={handleAppAction}
                appStore={appStore}
                variant="grid"
                responsive={responsive}
                installationProgress={appStore.installationProgress}
                className={responsive.isNarrow ? 'mt-4' : responsive.isMedium ? 'mt-6' : 'mt-8'}
              />
            </>
          )}

          {/* Search Results or Category Results */}
          {(searchQuery || selectedCategory !== 'discover') && (
            <AppStoreSection
              title={
                searchQuery
                  ? `Search Results`
                  : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Apps`
              }
              subtitle={
                searchQuery ? `Found ${filteredApps.length} apps` : `Apps for ${selectedCategory}`
              }
              apps={filteredApps}
              onAppAction={handleAppAction}
              appStore={appStore}
              variant={responsive.isNarrow ? 'list' : 'grid'}
              responsive={responsive}
              installationProgress={appStore.installationProgress}
              showEmpty={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}
